/**
 * Root application component for the web (shell/host) micro-frontend.
 *
 * Owns authentication flows (login, OTP verification), user onboarding
 * (profile completion, organization creation, invite code redemption),
 * the guest landing page, and the 404 catch-all.
 *
 * ## Onboarding
 *
 * New users who have no org memberships or class enrollments land on
 * `/onboard` after login. The onboarding routes are wrapped in
 * {@link AuthGuard} (requires a valid JWT) but intentionally have
 * **no** {@link ScopeGuard} — new users have zero scopes until they
 * create an org or redeem an invite code.
 *
 * The onboarding hub (`/onboard`) evaluates the user's state:
 * - Profile incomplete (no name) → redirect to `/onboard/profile`.
 * - Has scopes already → redirect to the appropriate zone.
 * - Profile complete, no scopes → show the track chooser.
 *
 * ## Zone navigation
 *
 * This shell does **not** provide zone navigation — the admin (`/admin`)
 * and student (`/learn`) zones are independent micro-frontends with their
 * own routing, layout chrome, and sidebars. The `/dashboard` convenience
 * route inspects the user's resolved scopes and redirects to the
 * appropriate zone (admin → student → onboard).
 *
 * Onboarding (profile complete: name + birthday) is enforced in all authed
 * areas: `/dashboard` and `/profile` are wrapped in {@link ProfileGuard},
 * which redirects to `/onboard/profile?returnTo=...` when profile is
 * incomplete. The admin and learn zones apply the same guard so users
 * cannot reach those zones until onboarding is complete. The `/profile`
 * route additionally requires at least one scope (see
 * {@link OnboardingCompleteGuard}); users with no scopes are redirected
 * to `/onboard`.
 *
 * @module app
 */

import { defaultRedirectForScopes } from '@learnai/api/auth';
import { AuthGuard, AuthLoading, ProfileGuard } from '@learnai/api/auth-guard';
import { useAuth } from '@learnai/api/auth-provider';
import { BrandLogo } from '@learnai/ui/components/blocks/brand-logo';
import { NotFound } from '@learnai/ui/components/blocks/not-found';
import { VersionBadge } from '@learnai/ui/components/blocks/version-badge';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { Layout } from '@web/components/layout';
import { AuthCallback } from '@web/routes/auth-callback';
import { Landing } from '@web/routes/landing';
import { Login } from '@web/routes/login';
import { OnboardCreateOrg } from '@web/routes/onboard-create-org';
import { OnboardHub } from '@web/routes/onboard-hub';
import { OnboardJoin } from '@web/routes/onboard-join';
import { OnboardProfile } from '@web/routes/onboard-profile';
import { Profile } from '@web/routes/profile';
import { Verify } from '@web/routes/verify';
import { useEffect } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';

// ---------------------------------------------------------------------------
// Smart redirect — resolves destination from user scopes
// ---------------------------------------------------------------------------

function DashboardRedirect() {
	const { isAuthenticated, isLoading, scopes } = useAuth();

	useEffect(() => {
		if (isLoading) return;

		if (!isAuthenticated) {
			window.location.href = '/login?returnTo=/dashboard';
			return;
		}

		const destination = defaultRedirectForScopes(scopes);
		window.location.href = destination;
	}, [isAuthenticated, isLoading, scopes]);

	return (
		<div className="flex min-h-screen items-center justify-center">
			<Spinner className="size-6 text-muted-foreground" />
		</div>
	);
}

// ---------------------------------------------------------------------------
// Onboarding layout — AuthGuard wrapper with no scope requirement
// ---------------------------------------------------------------------------

function ShellNotFound() {
	return (
		<div className="relative">
			<nav className="absolute top-6 left-6">
				<a href="/">
					<BrandLogo size="sm" />
				</a>
			</nav>
			<NotFound />
		</div>
	);
}

function OnboardLayout() {
	return (
		<AuthGuard>
			<Outlet />
		</AuthGuard>
	);
}

/**
 * Redirects to /onboard when the user has no scopes (onboarding incomplete).
 * Use after AuthGuard so profile is only reachable once onboarding is complete.
 */
function OnboardingCompleteGuard({ children }: { children: React.ReactNode }) {
	const { scopes, isLoading, isReady } = useAuth();

	useEffect(() => {
		if (isReady && !isLoading && defaultRedirectForScopes(scopes) === '/onboard') {
			window.location.href = '/onboard';
		}
	}, [isReady, isLoading, scopes]);

	if (isLoading || !isReady) {
		return <AuthLoading />;
	}

	if (defaultRedirectForScopes(scopes) === '/onboard') {
		return <AuthLoading />;
	}

	return <>{children}</>;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export function App() {
	return (
		<BrowserRouter>
			<Routes>
				{/* ── Public auth routes (no layout chrome) ─────────────── */}
				<Route path="/login" element={<Login />} />
				<Route path="/verify" element={<Verify />} />
				<Route path="/auth/callback" element={<AuthCallback />} />

				{/* ── Onboarding (auth required, no scope required) ─────── */}
				<Route element={<OnboardLayout />}>
					<Route path="/onboard" element={<OnboardHub />} />
					<Route path="/onboard/profile" element={<OnboardProfile />} />
					<Route path="/onboard/create-org" element={<OnboardCreateOrg />} />
					<Route path="/onboard/join" element={<OnboardJoin />} />
				</Route>

				{/* ── Shell layout wrapping all non-auth pages ──────────── */}
				<Route element={<Layout />}>
					<Route index element={<Landing />} />

					{/* Scope-aware redirect (auth + profile complete required) */}
					<Route
						path="/dashboard"
						element={
							<AuthGuard>
								<ProfileGuard>
									<DashboardRedirect />
								</ProfileGuard>
							</AuthGuard>
						}
					/>

					{/* Profile (auth + profile complete + at least one scope) — shared by admin and learn */}
					<Route
						path="/profile"
						element={
							<AuthGuard>
								<ProfileGuard>
									<OnboardingCompleteGuard>
										<Profile />
									</OnboardingCompleteGuard>
								</ProfileGuard>
							</AuthGuard>
						}
					/>
				</Route>

				{/* ── Catch-all 404 ─────────────────────────────────────── */}
				<Route path="*" element={<ShellNotFound />} />
			</Routes>
			<VersionBadge className="pointer-events-none fixed right-3 bottom-2 z-50" />
		</BrowserRouter>
	);
}
